import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0A0F1F]" data-testid="privacy-policy-page">
      <div className="border-b border-white/[0.04] bg-[#0A0F1F]/95 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg font-extrabold text-white">Site<span className="text-slate-500 font-medium">Smith</span></Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back to Home
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16 sm:py-24">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2 tracking-tight" data-testid="privacy-policy-title">Privacy Policy</h1>
        <p className="text-slate-500 text-sm mb-12">Last Updated: April 19, 2026</p>

        <div className="prose-sm text-slate-400 leading-relaxed space-y-8">
          <section>
            <h2 className="text-xl font-bold text-white mb-3">1. Introduction</h2>
            <p>SiteSmith ("we," "us," or "our") operates the website sitesmithing.com and provides custom website design, hosting, security, and DDoS protection services. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you visit our website or use our services.</p>
            <p className="mt-3">By using our services, you agree to the collection and use of information as described in this policy. If you do not agree, please do not use our services.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">2. Information We Collect</h2>
            <h3 className="text-base font-semibold text-slate-300 mb-2">2.1 Information You Provide Directly</h3>
            <p>When you sign up for our services or contact us, we may collect:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Your name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Business name and type</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
              <li>Any additional information you provide in forms, emails, or communications with us</li>
            </ul>

            <h3 className="text-base font-semibold text-slate-300 mt-4 mb-2">2.2 Information Collected Automatically</h3>
            <p>When you visit sitesmithing.com, we may automatically collect:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device type and operating system</li>
              <li>Pages visited, time spent on pages, and navigation patterns</li>
              <li>Referring website or source</li>
            </ul>

            <h3 className="text-base font-semibold text-slate-300 mt-4 mb-2">2.3 Information Collected from Client Website Visitors</h3>
            <p>If SiteSmith hosts analytics or tracking tools on websites we build and host for clients, we may collect basic visitor data such as page views, traffic sources, and device information on behalf of our clients. This data is used solely to provide analytics insights to the client and is not sold or shared with third parties.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Design, build, and host your custom website</li>
              <li>Process payments and manage your subscription</li>
              <li>Communicate with you about your account, services, and support requests</li>
              <li>Improve our website and services</li>
              <li>Send you updates about your website or account (not marketing spam)</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p className="mt-3">We do not sell your personal information to third parties. Period.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">4. Payment Information</h2>
            <p>All payment processing is handled by third-party payment processors. <strong className="text-slate-300">SiteSmith does not store your credit card numbers, bank account details, or other sensitive financial information on our servers.</strong> Our payment processors are PCI-DSS compliant and use industry-standard encryption to protect your data.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">5. Cookies and Tracking Technologies</h2>
            <p>Sitesmithing.com may use cookies and similar tracking technologies to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Remember your preferences and settings</li>
              <li>Understand how visitors use our website</li>
              <li>Analyze traffic and improve our website performance</li>
            </ul>
            <p className="mt-3">You can control cookies through your browser settings. Disabling cookies may affect certain features of our website but will not prevent you from using our services.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">6. Third-Party Services</h2>
            <p>We use the following categories of third-party service providers:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong className="text-slate-300">Payment processors</strong> to handle billing and subscription management</li>
              <li><strong className="text-slate-300">Hosting infrastructure providers</strong> to deliver reliable, fast website hosting</li>
              <li><strong className="text-slate-300">Analytics tools</strong> to understand website traffic and improve our services</li>
              <li><strong className="text-slate-300">Email service providers</strong> to deliver transactional communications</li>
            </ul>
            <p className="mt-3">Each third-party provider has their own privacy policy governing how they handle your data. We only work with providers who meet reasonable security and privacy standards.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">7. Data Retention</h2>
            <p>We retain your personal information for as long as your account is active or as needed to provide you with our services. After you cancel your subscription:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>We may retain your contact information and account records for up to 12 months for administrative purposes and to comply with legal obligations.</li>
              <li>Website files, designs, and content may be retained in our backups for up to 90 days after cancellation.</li>
              <li>Payment records may be retained as required by tax and accounting regulations.</li>
            </ul>
            <p className="mt-3">If you request deletion of your data, we will process your request within 30 days, subject to any legal retention requirements.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">8. Security Measures</h2>
            <p>We take the protection of your data seriously and implement reasonable administrative, technical, and physical safeguards, including:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>SSL/TLS encryption for all data transmitted to and from our servers</li>
              <li>DDoS protection on all hosted websites</li>
              <li>Regular security audits and software updates</li>
              <li>Access controls limiting who can view client data internally</li>
            </ul>
            <p className="mt-3">No method of electronic transmission or storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">9. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong className="text-slate-300">Access</strong> the personal information we hold about you</li>
              <li><strong className="text-slate-300">Correct</strong> inaccurate or incomplete information</li>
              <li><strong className="text-slate-300">Request deletion</strong> of your personal information (subject to legal retention requirements)</li>
              <li><strong className="text-slate-300">Opt out</strong> of marketing communications at any time</li>
              <li><strong className="text-slate-300">Request a copy</strong> of your data in a portable format</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, contact us at the information provided below. We will respond to your request within 30 days.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">10. California Consumer Privacy Act (CCPA)</h2>
            <p>If you are a California resident, you have additional rights under the CCPA, including:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>The right to know what personal information we collect, use, and disclose</li>
              <li>The right to request deletion of your personal information</li>
              <li>The right to opt out of the sale of your personal information (we do not sell your data)</li>
              <li>The right to non-discrimination for exercising your privacy rights</li>
            </ul>
            <p className="mt-3">To make a CCPA request, contact us using the information below.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">11. Children's Privacy</h2>
            <p>SiteSmith does not knowingly collect personal information from children under the age of 13. If we become aware that we have collected data from a child under 13, we will take steps to delete that information promptly. If you believe a child under 13 has provided us with personal information, please contact us immediately.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">12. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. When we make changes, we will update the "Last Updated" date at the top of this page. For material changes, we will notify you via email or through a notice on our website. Your continued use of our services after changes are posted constitutes your acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">13. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or want to exercise your privacy rights, contact us at:</p>
            <div className="mt-3 p-4 border border-white/[0.06] rounded-lg bg-[#111827]">
              <p className="text-slate-300 font-semibold">SiteSmith</p>
              <p>Austin & Jack</p>
              <p>Arizona, United States</p>
              <p className="mt-2">Email: <a href="mailto:hello@sitesmithing.com" className="text-orange-500 hover:underline">hello@sitesmithing.com</a></p>
              <p>Website: <a href="https://sitesmithing.com" className="text-orange-500 hover:underline">sitesmithing.com</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
